import type {ComposerAttachmentIcon} from '@github-ui/agent-sessions-interface/components/chat-composer-attachments'
import type {MessageAttachment, MessageAttachmentKind} from '@microsoft/agent-host-protocol'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

const SIMPLE_ATTACHMENT = 'simple' as MessageAttachmentKind.Simple
const ATTACHMENT_DETAIL_META_KEY = 'copilot.attachmentDetail'
const ATTACHMENT_UI_META_KEY = 'github.attachmentUi'

export type ReferenceType = 'issue' | 'pr' | 'discussion'

export const REFERENCE_TYPE_ANNOUNCE_LABEL: Record<ReferenceType, string> = {
  issue: 'issue',
  pr: 'pull request',
  discussion: 'discussion',
}

export function githubReferenceDetails(
  attachment: MessageAttachment,
): {referenceType: ReferenceType; number: number} | undefined {
  const detail = attachment._meta?.[ATTACHMENT_DETAIL_META_KEY] as
    | {type?: string; referenceType?: string; number?: unknown}
    | undefined
  if (
    detail?.type !== 'github_reference' ||
    !['issue', 'pr', 'discussion'].includes(detail.referenceType ?? '') ||
    typeof detail.number !== 'number'
  )
    return undefined
  return {referenceType: detail.referenceType as ReferenceType, number: detail.number}
}

function absoluteUrl(pathOrUrl: string | undefined): string | undefined {
  if (!pathOrUrl) return undefined
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl
  const origin = ssrSafeLocation.origin || 'https://github.com/'
  return `${origin}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`
}

function simpleAttachment(
  label: string,
  displayKind: string,
  detail: Record<string, unknown>,
  uiMeta?: Record<string, unknown>,
): MessageAttachment {
  return {
    type: SIMPLE_ATTACHMENT,
    label,
    displayKind,
    _meta: {
      [ATTACHMENT_DETAIL_META_KEY]: detail,
      ...(uiMeta ? {[ATTACHMENT_UI_META_KEY]: uiMeta} : {}),
    },
  }
}

export function githubReference(
  referenceType: ReferenceType,
  number: number,
  url: string | undefined,
  title?: string,
  state?: string,
  draft?: boolean,
  privateRepository?: boolean,
): MessageAttachment | undefined {
  const absoluteReferenceUrl = absoluteUrl(url)
  if (!absoluteReferenceUrl) return undefined
  const label = title ?? `#${number}`
  const uiMeta = {
    ...(draft ? {draft} : {}),
    ...(privateRepository === undefined ? {} : {private: privateRepository}),
  }
  return simpleAttachment(
    label,
    'github-reference',
    {
      type: 'github_reference',
      number,
      referenceType,
      title: label,
      state: state ?? '',
      url: absoluteReferenceUrl,
    },
    Object.keys(uiMeta).length > 0 ? uiMeta : undefined,
  )
}

export function implicitAttachmentIcon(attachment: MessageAttachment): ComposerAttachmentIcon {
  switch (attachment.displayKind) {
    case 'repository':
      return 'repo'
    case 'github-commit':
      return 'commit'
    case 'github-release':
      return 'release'
    case 'github-snippet':
      return 'snippet'
    case 'github-file':
      return 'file'
    case 'github-file-diff':
      return 'diff'
    case 'github-url':
      return 'link'
    case 'github-actions-job':
      return 'workflow'
  }

  const detail = attachment._meta?.[ATTACHMENT_DETAIL_META_KEY] as
    | {type?: string; referenceType?: string; state?: string}
    | undefined
  const uiMeta = attachment._meta?.[ATTACHMENT_UI_META_KEY] as {draft?: boolean} | undefined
  if (detail?.type === 'github_reference') {
    if (detail.referenceType === 'pr') {
      if (uiMeta?.draft && (detail.state === 'open' || !detail.state)) return 'pull-draft'
      if (detail.state === 'open') return 'pull-open'
      if (detail.state === 'closed') return 'pull-closed'
      if (detail.state === 'merged') return 'pull-merged'
      return 'pull'
    }
    if (detail.referenceType === 'discussion') return 'discussion'
    if (detail.referenceType === 'issue') {
      if (detail.state === 'open') return 'issue-open'
      if (detail.state === 'closed') return 'issue-closed'
      if (detail.state === 'not_planned') return 'issue-not-planned'
      return 'issue'
    }
  }
  return 'file'
}

export function implicitAttachmentUrl(attachment: MessageAttachment): string | undefined {
  const detail = attachment._meta?.[ATTACHMENT_DETAIL_META_KEY] as {url?: string} | undefined
  return typeof detail?.url === 'string' ? detail.url : undefined
}

export function implicitAttachmentKey(attachment: MessageAttachment): string {
  const detail = attachment._meta?.[ATTACHMENT_DETAIL_META_KEY] as
    | {type?: string; path?: string; ref?: string; repo?: {owner?: string; name?: string}}
    | undefined
  if (detail?.type === 'github_snippet' && detail.path) {
    return `github_snippet:${detail.repo?.owner ?? ''}/${detail.repo?.name ?? ''}:${detail.ref ?? ''}:${detail.path}`
  }
  return implicitAttachmentUrl(attachment) ?? `${attachment.displayKind ?? ''}:${attachment.label ?? ''}`
}
